import * as React from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

import { cn } from '../lib/cn';
import { dedupeById } from '../lib/dedupeById';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface AutocompleteSingleSelectOption {
  id: string;
  label: string;
  disabled?: boolean | undefined;
}

export interface AutocompleteSingleSelectProps {
  label: string;
  options: AutocompleteSingleSelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  onSearchTermChange?: ((term: string) => void) | undefined;
  searchDebounceMs?: number | undefined;
  loading?: boolean | undefined;
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyMessage?: string | undefined;
  minSearchLength?: number | undefined;
  maxResults?: number | undefined;
  disabled?: boolean | undefined;
  clearable?: boolean | undefined;
  className?: string | undefined;
}

export function AutocompleteSingleSelect({
  label,
  options,
  value,
  onValueChange,
  onSearchTermChange,
  searchDebounceMs = 0,
  loading = false,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search options',
  emptyMessage = 'No options found.',
  minSearchLength = 0,
  maxResults = 50,
  disabled = false,
  clearable = true,
  className,
}: AutocompleteSingleSelectProps) {
  const listboxId = React.useId();
  const controlRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchChangedRef = React.useRef(false);
  const onSearchTermChangeRef = React.useRef(onSearchTermChange);
  const [open, setOpen] = React.useState(false);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  const [query, setQuery] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [waitingForServerSearch, setWaitingForServerSearch] = React.useState(false);
  const normalizedQuery = query.trim().toLowerCase();
  const resolvedOptions = React.useMemo(() => dedupeById(options), [options]);
  const selectedOption = resolvedOptions.find((option) => option.id === value);
  const resolvingSelectedValue = loading && Boolean(value) && !selectedOption;
  const isServerSearch = Boolean(onSearchTermChange);
  const shouldShowResults = isServerSearch ? normalizedQuery.length >= minSearchLength : true;
  const effectiveLoading = loading || waitingForServerSearch;
  const effectiveDisabled = disabled || resolvingSelectedValue;
  const effectiveOpen = open && !effectiveDisabled;
  const visibleOptions = React.useMemo(() => {
    if (!shouldShowResults) return [];
    if (isServerSearch) return resolvedOptions.slice(0, maxResults);
    return resolvedOptions
      .filter((option) => option.label.toLowerCase().includes(normalizedQuery))
      .slice(0, maxResults);
  }, [isServerSearch, maxResults, normalizedQuery, resolvedOptions, shouldShowResults]);

  React.useEffect(() => {
    onSearchTermChangeRef.current = onSearchTermChange;
  }, [onSearchTermChange]);

  React.useEffect(() => {
    if (!onSearchTermChangeRef.current || !searchChangedRef.current) return;
    const timeoutId = window.setTimeout(() => {
      searchChangedRef.current = false;
      const term = query.trim();
      onSearchTermChangeRef.current?.(term.length >= minSearchLength ? term : '');
      setWaitingForServerSearch(false);
    }, searchDebounceMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [minSearchLength, query, searchDebounceMs]);

  function changeSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const nextQuery = event.target.value;
    searchChangedRef.current = isServerSearch;
    setQuery(nextQuery);
    setHighlightedIndex(-1);
    setWaitingForServerSearch(isServerSearch && nextQuery.trim().length >= minSearchLength);
  }

  function selectOption(option: AutocompleteSingleSelectOption) {
    if (option.disabled) return;
    onValueChange(option.id);
    close();
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' && visibleOptions.length) {
      event.preventDefault();
      setHighlightedIndex((current) => (current < visibleOptions.length - 1 ? current + 1 : 0));
    }
    if (event.key === 'ArrowUp' && highlightedIndex >= 0) {
      event.preventDefault();
      setHighlightedIndex((current) => (current > 0 ? current - 1 : -1));
    }
    if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      const option = visibleOptions[highlightedIndex];
      if (option) selectOption(option);
    }
    if (event.key === 'Escape') close();
  }

  function close() {
    setOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
    setWaitingForServerSearch(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setPortalContainer(controlRef.current?.closest('[role="dialog"]') ?? null);
      setOpen(true);
    } else {
      close();
    }
  }

  return (
    <Popover open={effectiveOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          ref={controlRef}
          className={cn('border-input bg-card flex h-9 min-w-0 rounded-md border', className)}
        >
          <button
            type="button"
            role="combobox"
            aria-label={label}
            aria-busy={resolvingSelectedValue || undefined}
            aria-controls={effectiveOpen && shouldShowResults ? listboxId : undefined}
            aria-expanded={effectiveOpen}
            aria-haspopup="listbox"
            aria-activedescendant={
              effectiveOpen && highlightedIndex >= 0
                ? `${listboxId}-option-${String(highlightedIndex)}`
                : undefined
            }
            disabled={effectiveDisabled}
            className="text-foreground flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span
              className={cn('min-w-0 flex-1 truncate', !selectedOption && 'text-muted-foreground')}
            >
              {resolvingSelectedValue ? 'Loading…' : (selectedOption?.label ?? placeholder)}
            </span>
            <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
          {value && clearable ? (
            <button
              type="button"
              aria-label={`Clear ${label}`}
              disabled={effectiveDisabled}
              className="text-muted-foreground hover:text-foreground mr-2 shrink-0 cursor-pointer disabled:cursor-not-allowed"
              onClick={(event) => {
                event.stopPropagation();
                onValueChange('');
              }}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        portalContainer={portalContainer}
        className="z-[60] w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className={cn('p-2', shouldShowResults && 'border-border border-b')}>
          <div className="relative">
            <Search
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2"
              aria-hidden="true"
            />
            <input
              ref={searchInputRef}
              value={query}
              aria-label={searchPlaceholder}
              aria-controls={shouldShowResults ? listboxId : undefined}
              aria-activedescendant={
                highlightedIndex >= 0
                  ? `${listboxId}-option-${String(highlightedIndex)}`
                  : undefined
              }
              aria-autocomplete="list"
              placeholder={searchPlaceholder}
              className="border-input bg-card focus:ring-ring h-8 w-full rounded-md border pr-2 pl-8 text-sm outline-none focus:ring-1"
              onChange={changeSearch}
              onKeyDown={handleInputKeyDown}
            />
          </div>
        </div>
        {shouldShowResults ? (
          <div
            id={listboxId}
            role="listbox"
            aria-label={label}
            className="max-h-64 overflow-y-auto p-1"
          >
            {effectiveLoading ? (
              <p className="text-muted-foreground px-2 py-6 text-center text-sm">Searching…</p>
            ) : null}
            {!effectiveLoading && !visibleOptions.length ? (
              <p className="text-muted-foreground px-2 py-6 text-center text-sm">{emptyMessage}</p>
            ) : null}
            {!effectiveLoading
              ? visibleOptions.map((option, index) => {
                  const selected = option.id === value;
                  return (
                    <button
                      key={option.id}
                      id={`${listboxId}-option-${String(index)}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={option.disabled}
                      className={cn(
                        'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none disabled:pointer-events-none disabled:opacity-50',
                        highlightedIndex === index && 'bg-accent text-accent-foreground',
                      )}
                      onPointerMove={() => {
                        setHighlightedIndex(index);
                      }}
                      onClick={() => {
                        selectOption(option);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                      {selected ? <Check className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
                    </button>
                  );
                })
              : null}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
